'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FeedbackFormData {
  coachingQuality: number;
  platformUsability: number;
  overallSatisfaction: number;
  coachingEffectiveness: string;
  featureRequests: string;
  improvementSuggestions: string;
  feedbackCategory: string;
  coachingRelevance: string;
  investorReadinessAccuracy: string;
  platformNavigation: string;
  contactName: string;
  contactEmail: string;
  allowFollowUp: boolean;
  isAnonymous: boolean;
}

interface FeedbackFormProps {
  onSubmit: (data: FeedbackFormData) => void;
  isSubmitting: boolean;
}

const FeedbackForm = ({ onSubmit, isSubmitting }: FeedbackFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FeedbackFormData>({
    coachingQuality: 0,
    platformUsability: 0,
    overallSatisfaction: 0,
    coachingEffectiveness: '',
    featureRequests: '',
    improvementSuggestions: '',
    feedbackCategory: '',
    coachingRelevance: '',
    investorReadinessAccuracy: '',
    platformNavigation: '',
    contactName: '',
    contactEmail: '',
    allowFollowUp: false,
    isAnonymous: false,
  });

  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [errors, setErrors] = useState<Partial<Record<keyof FeedbackFormData, string>>>({});

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const totalFields = 13;
    let filledFields = 0;

    if (formData.coachingQuality > 0) filledFields++;
    if (formData.platformUsability > 0) filledFields++;
    if (formData.overallSatisfaction > 0) filledFields++;
    if (formData.coachingEffectiveness.trim()) filledFields++;
    if (formData.featureRequests.trim()) filledFields++;
    if (formData.improvementSuggestions.trim()) filledFields++;
    if (formData.feedbackCategory) filledFields++;
    if (formData.coachingRelevance) filledFields++;
    if (formData.investorReadinessAccuracy) filledFields++;
    if (formData.platformNavigation) filledFields++;
    if (!formData.isAnonymous && formData.contactName.trim()) filledFields++;
    if (!formData.isAnonymous && formData.contactEmail.trim()) filledFields++;
    if (formData.allowFollowUp) filledFields++;

    setCompletionPercentage(Math.round((filledFields / totalFields) * 100));

    const savedData = localStorage.getItem('feedbackFormDraft');
    if (savedData && !formData.coachingQuality) {
      setFormData(JSON.parse(savedData));
    }
  }, [isHydrated, formData]);

  useEffect(() => {
    if (!isHydrated) return;
    
    const autoSaveTimer = setTimeout(() => {
      localStorage.setItem('feedbackFormDraft', JSON.stringify(formData));
    }, 1000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, isHydrated]);

  const handleRatingChange = (field: 'coachingQuality' | 'platformUsability' | 'overallSatisfaction', value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleInputChange = (field: keyof FeedbackFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FeedbackFormData, string>> = {};

    if (formData.coachingQuality === 0) {
      newErrors.coachingQuality = 'Por favor califica la calidad del coaching';
    }
    if (formData.platformUsability === 0) {
      newErrors.platformUsability = 'Por favor califica la usabilidad de la plataforma';
    }
    if (formData.overallSatisfaction === 0) {
      newErrors.overallSatisfaction = 'Por favor califica tu satisfacción general';
    }
    if (!formData.feedbackCategory) {
      newErrors.feedbackCategory = 'Por favor selecciona una categoría';
    }
    if (!formData.isAnonymous && !formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Por favor proporciona tu correo electrónico';
    }
    if (!formData.isAnonymous && formData.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Por favor proporciona un correo electrónico válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
      if (isHydrated) {
        localStorage.removeItem('feedbackFormDraft');
      }
    }
  };

  const renderStarRating = (field: 'coachingQuality' | 'platformUsability' | 'overallSatisfaction', label: string) => {
    const value = formData[field];
    
    return (
      <div className="space-y-2">
        <label className="block font-medium text-foreground">
          {label} <span className="text-error">*</span>
        </label>
        <div className="flex items-center space-x-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => handleRatingChange(field, star)}
              className="transition-smooth hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary rounded"
              aria-label={`Calificar ${star} de 5 estrellas`}
            >
              <Icon
                name="StarIcon"
                variant={star <= value ? 'solid' : 'outline'}
                size={32}
                className={star <= value ? 'text-accent' : 'text-muted-foreground'}
              />
            </button>
          ))}
          <span className="ml-4 caption text-muted-foreground">
            {value > 0 ? `${value}/5` : 'Sin calificar'}
          </span>
        </div>
        {errors[field] && (
          <p className="caption text-error flex items-center space-x-1">
            <Icon name="ExclamationCircleIcon" size={16} />
            <span>{errors[field]}</span>
          </p>
        )}
      </div>
    );
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6 lg:p-8">
        <div className="space-y-6">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-32 bg-muted animate-pulse rounded" />
          <div className="h-32 bg-muted animate-pulse rounded" />
          <div className="h-32 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-lg shadow-elevation-md p-6 lg:p-8 space-y-8">
      {/* Progress Indicator */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="caption text-muted-foreground">Progreso del formulario</span>
          <span className="caption font-medium text-primary">{completionPercentage}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-smooth"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Rating Section */}
      <div className="space-y-6">
        <h3 className="text-xl font-heading font-semibold text-foreground">
          Calificaciones
        </h3>
        
        {renderStarRating('coachingQuality', 'Calidad del Coaching de IA')}
        {renderStarRating('platformUsability', 'Usabilidad de la Plataforma')}
        {renderStarRating('overallSatisfaction', 'Satisfacción General')}
      </div>

      {/* Feedback Category */}
      <div className="space-y-2">
        <label htmlFor="feedbackCategory" className="block font-medium text-foreground">
          Categoría de Retroalimentación <span className="text-error">*</span>
        </label>
        <select
          id="feedbackCategory"
          value={formData.feedbackCategory}
          onChange={(e) => handleInputChange('feedbackCategory', e.target.value)}
          className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
        >
          <option value="">Selecciona una categoría</option>
          <option value="coaching-content">Contenido del Coaching</option>
          <option value="technical-issues">Problemas Técnicos</option>
          <option value="billing-concerns">Preocupaciones de Facturación</option>
          <option value="feature-requests">Solicitudes de Funciones</option>
          <option value="other">Otro</option>
        </select>
        {errors.feedbackCategory && (
          <p className="caption text-error flex items-center space-x-1">
            <Icon name="ExclamationCircleIcon" size={16} />
            <span>{errors.feedbackCategory}</span>
          </p>
        )}
      </div>

      {/* Open Text Areas */}
      <div className="space-y-6">
        <h3 className="text-xl font-heading font-semibold text-foreground">
          Retroalimentación Detallada
        </h3>

        <div className="space-y-2">
          <label htmlFor="coachingEffectiveness" className="block font-medium text-foreground">
            ¿Qué tan efectivo fue el coaching para tu negocio?
          </label>
          <textarea
            id="coachingEffectiveness"
            value={formData.coachingEffectiveness}
            onChange={(e) => handleInputChange('coachingEffectiveness', e.target.value)}
            placeholder="Comparte tu experiencia sobre cómo el coaching de IA te ayudó a mejorar tu negocio..."
            rows={4}
            className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="featureRequests" className="block font-medium text-foreground">
            ¿Qué funciones te gustaría ver en la plataforma?
          </label>
          <textarea
            id="featureRequests"
            value={formData.featureRequests}
            onChange={(e) => handleInputChange('featureRequests', e.target.value)}
            placeholder="Describe las funciones o mejoras que te gustaría que agregáramos..."
            rows={4}
            className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="improvementSuggestions" className="block font-medium text-foreground">
            Sugerencias de Mejora
          </label>
          <textarea
            id="improvementSuggestions"
            value={formData.improvementSuggestions}
            onChange={(e) => handleInputChange('improvementSuggestions', e.target.value)}
            placeholder="¿Cómo podemos mejorar tu experiencia con LynMet?..."
            rows={4}
            className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
          />
        </div>
      </div>

      {/* User Experience Questions */}
      <div className="space-y-6">
        <h3 className="text-xl font-heading font-semibold text-foreground">
          Experiencia de Usuario
        </h3>

        <div className="space-y-2">
          <label htmlFor="coachingRelevance" className="block font-medium text-foreground">
            ¿Qué tan relevante fue el coaching para tu industria?
          </label>
          <select
            id="coachingRelevance"
            value={formData.coachingRelevance}
            onChange={(e) => handleInputChange('coachingRelevance', e.target.value)}
            className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
          >
            <option value="">Selecciona una opción</option>
            <option value="very-relevant">Muy Relevante</option>
            <option value="somewhat-relevant">Algo Relevante</option>
            <option value="neutral">Neutral</option>
            <option value="not-very-relevant">No Muy Relevante</option>
            <option value="not-relevant">No Relevante</option>
          </select>
        </div>

        <div className="space-y-2">
          <label htmlFor="investorReadinessAccuracy" className="block font-medium text-foreground">
            ¿Qué tan preciso fue el puntaje de preparación para inversores?
          </label>
          <select
            id="investorReadinessAccuracy"
            value={formData.investorReadinessAccuracy}
            onChange={(e) => handleInputChange('investorReadinessAccuracy', e.target.value)}
            className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
          >
            <option value="">Selecciona una opción</option>
            <option value="very-accurate">Muy Preciso</option>
            <option value="somewhat-accurate">Algo Preciso</option>
            <option value="neutral">Neutral</option>
            <option value="not-very-accurate">No Muy Preciso</option>
            <option value="not-accurate">No Preciso</option>
          </select>
        </div>

        <div className="space-y-2">
          <label htmlFor="platformNavigation" className="block font-medium text-foreground">
            ¿Qué tan fácil fue navegar por la plataforma?
          </label>
          <select
            id="platformNavigation"
            value={formData.platformNavigation}
            onChange={(e) => handleInputChange('platformNavigation', e.target.value)}
            className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
          >
            <option value="">Selecciona una opción</option>
            <option value="very-easy">Muy Fácil</option>
            <option value="easy">Fácil</option>
            <option value="neutral">Neutral</option>
            <option value="difficult">Difícil</option>
            <option value="very-difficult">Muy Difícil</option>
          </select>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-heading font-semibold text-foreground">
            Información de Contacto (Opcional)
          </h3>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isAnonymous}
              onChange={(e) => handleInputChange('isAnonymous', e.target.checked)}
              className="w-4 h-4 text-primary bg-background border-input rounded focus:ring-2 focus:ring-primary"
            />
            <span className="caption text-muted-foreground">Enviar de forma anónima</span>
          </label>
        </div>

        {!formData.isAnonymous && (
          <>
            <div className="space-y-2">
              <label htmlFor="contactName" className="block font-medium text-foreground">
                Nombre Completo
              </label>
              <input
                type="text"
                id="contactName"
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                placeholder="Tu nombre completo"
                className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="contactEmail" className="block font-medium text-foreground">
                Correo Electrónico <span className="text-error">*</span>
              </label>
              <input
                type="email"
                id="contactEmail"
                value={formData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                placeholder="tu@correo.com"
                className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
              />
              {errors.contactEmail && (
                <p className="caption text-error flex items-center space-x-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  <span>{errors.contactEmail}</span>
                </p>
              )}
            </div>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allowFollowUp}
                onChange={(e) => handleInputChange('allowFollowUp', e.target.checked)}
                className="mt-1 w-4 h-4 text-primary bg-background border-input rounded focus:ring-2 focus:ring-primary"
              />
              <span className="caption text-muted-foreground">
                Permito que el equipo de LynMet se comunique conmigo para discutir mi retroalimentación en detalle
              </span>
            </label>
          </>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4 pt-6 border-t border-border">
        <p className="caption text-muted-foreground">
          * Campos obligatorios
        </p>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-8 py-3 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 active:scale-97 transition-smooth shadow-elevation-sm hover:shadow-elevation-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Enviando...</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} />
              <span>Enviar Retroalimentación</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default FeedbackForm;
